import type { TiptapDoc } from '@odo/shared-ui';
import apiV2 from '../api/v2';
import type { PaginatedMeta } from './accounts';

// Accounts > Communications (admin-only /api/v2/practices/communications). A
// Communication is a thread that owns dated Entries on a timeline.

export type CommunicationChannel = 'call' | 'text' | 'email' | 'dashboard' | 'note';
export type EntryDirection = 'incoming' | 'outgoing';
export type CommunicationStatus = 'open' | 'resolved';

// The literal query-param value meaning "practiceId IS NULL" - threads
// logged before the practice was identified.
export const UNASSIGNED_PRACTICE_FILTER = 'unassigned';
// The literal `assignee` query-param value meaning "assignee_id IS NULL" -
// open work nobody has picked up yet.
export const UNASSIGNED_ASSIGNEE_FILTER = 'unassigned';

export interface CommunicationPatientRef {
    id: string;
    name: string;
    profileThumbnailUrl: string | null;
}

// Resolved by join server-side on every read, never denormalized.
// Email-keyed on write, like conversation assignment.
export interface CommunicationAssignee {
    id: string;
    email: string;
    fullname: string;
}

// One patient note created from this entry (see EntryRowMenu's "Add patient
// note"). `severity` mirrors patient-files' own note severity.
export interface CommunicationEntryPatientNote {
    id: string;
    patientId: string;
    patientName: string;
    severity: 'info' | 'warning' | 'critical';
}

export interface CommunicationEntry {
    id: string;
    communicationId: string;
    channel: CommunicationChannel;
    // Optional headline for this specific interaction - distinct from the
    // thread's own title. Null is common (most entries don't need one).
    title: string | null;
    // Null only on a 'note' entry - every other channel always carries one.
    direction: EntryDirection | null;
    body: string | null;
    bodyDoc: TiptapDoc | null;
    // Generalises the old callNumber - phone for call/text, email address for email.
    contact: string | null;
    // Generalises the old callerName.
    contactName: string | null;
    /** Whole minutes, >= 1. Only ever set on a 'call' entry. */
    durationMinutes: number | null;
    occurredAt: string;
    authorId: string;
    authorName: string;
    createdAt: string;
    updatedAt: string;
    /** Optional until the backend lands it - default to `[]` when reading. */
    patientNotes?: CommunicationEntryPatientNote[];
}

export interface CommunicationEntryCountByChannel {
    channel: CommunicationChannel;
    count: number;
}

export interface CommunicationLastEntry {
    id: string;
    channel: CommunicationChannel;
    title: string | null;
    direction: EntryDirection | null;
    occurredAt: string;
    authorName: string;
    // Server-built plain-text preview - the entry's own title if it has one,
    // else its body truncated to 160 chars, else null (neither exists, e.g.
    // a bare call log with no notes). Never re-derived client-side.
    snippet: string | null;
}

export interface Communication {
    id: string;
    practiceId: string | null;
    // Denormalized alongside practiceId for list rows that span practices -
    // null exactly when practiceId is null, never populated separately.
    practiceName: string | null;
    referenceNo: string;
    title: string;
    status: CommunicationStatus;
    // Validated server-side against the predefined list (see categories.ts)
    // and deduped there too - never assume this arrives sorted or unique.
    categories: string[];
    authorId: string;
    authorName: string;
    // Null when nobody owes follow-up - the common case, not a missing value.
    assignee: CommunicationAssignee | null;
    patients: CommunicationPatientRef[];
    entryCount: number;
    // Live entries only, ordered by count desc; never re-sorted client-side.
    entryCountsByChannel: CommunicationEntryCountByChannel[];
    lastEntry: CommunicationLastEntry | null;
    lastActivityAt: string;
    createdAt: string;
    updatedAt: string;
}

export interface CommunicationDetail extends Communication {
    /** Ascending by occurredAt - the timeline's render order. */
    entries: CommunicationEntry[];
}

export interface CommunicationsListParams {
    page: number;
    limit: number;
    // A real practice id, or the literal "unassigned" filter above. Omitted
    // entirely means no practice filter.
    practice?: string;
    status?: CommunicationStatus;
    // Single value - the list endpoint's `category` filter is array-contains
    // against one category, not a multi-select like status.
    category?: string;
    // An ODO user's email, or the literal "unassigned" above.
    assignee?: string;
    patient?: string;
    channel?: CommunicationChannel;
    search?: string;
    // Omitted stays valid - the backend defaults to lastActivityAt desc.
    sort?: 'lastActivityAt';
    order?: 'asc' | 'desc';
}

export interface CreateEntryInput {
    channel: CommunicationChannel;
    // Optional headline for this interaction - trimmed, <= 200 chars.
    title?: string;
    // Required unless channel is 'note'.
    direction?: EntryDirection;
    body?: string;
    bodyDoc?: TiptapDoc;
    contact?: string;
    contactName?: string;
    /** Whole minutes, >= 1. Only valid on channel 'call'. */
    durationMinutes?: number;
    /** ISO 8601 timestamp. */
    occurredAt: string;
}

export interface CreateCommunicationInput {
    practiceId?: string;
    // Client-generated (see generateReferenceNo.ts); a 409 means a collision.
    referenceNo?: string;
    title: string;
    status?: CommunicationStatus;
    categories?: string[];
    assigneeEmail?: string;
    patientIds?: string[];
    entry: CreateEntryInput;
}

export interface UpdateCommunicationInput {
    practiceId?: string | null;
    title?: string;
    status?: CommunicationStatus;
    // Full replacement set - omitted means "unchanged", `[]` clears every link/category.
    categories?: string[];
    patientIds?: string[];
    // null explicitly unassigns; omitted leaves the current assignee alone.
    assigneeEmail?: string | null;
}

export interface UpdateEntryInput {
    channel?: CommunicationChannel;
    // null explicitly clears the headline; omitted leaves it unchanged.
    title?: string | null;
    direction?: EntryDirection | null;
    body?: string | null;
    bodyDoc?: TiptapDoc | null;
    contact?: string | null;
    contactName?: string | null;
    durationMinutes?: number | null;
    occurredAt?: string;
}

const BASE_PATH = '/practices/communications';

export async function fetchCommunications(
    params: CommunicationsListParams
): Promise<{ data: Communication[]; meta: PaginatedMeta }> {
    const response = await apiV2.get<{
        success: true;
        data: Communication[];
        meta: PaginatedMeta;
    }>(BASE_PATH, { params });
    return { data: response.data.data, meta: response.data.meta };
}

export async function fetchCommunication(id: string): Promise<CommunicationDetail> {
    const response = await apiV2.get<{ success: true; data: CommunicationDetail }>(
        `${BASE_PATH}/${id}`
    );
    return response.data.data;
}

export async function createCommunication(
    input: CreateCommunicationInput
): Promise<CommunicationDetail> {
    const response = await apiV2.post<{ success: true; data: CommunicationDetail }>(
        BASE_PATH,
        input
    );
    return response.data.data;
}

export async function updateCommunication(
    id: string,
    patch: UpdateCommunicationInput
): Promise<CommunicationDetail> {
    const response = await apiV2.patch<{ success: true; data: CommunicationDetail }>(
        `${BASE_PATH}/${id}`,
        patch
    );
    return response.data.data;
}

export async function deleteCommunication(id: string): Promise<void> {
    await apiV2.delete(`${BASE_PATH}/${id}`);
}

export async function addCommunicationEntry(
    id: string,
    input: CreateEntryInput
): Promise<CommunicationDetail> {
    const response = await apiV2.post<{ success: true; data: CommunicationDetail }>(
        `${BASE_PATH}/${id}/entries`,
        input
    );
    return response.data.data;
}

export async function updateCommunicationEntry(
    id: string,
    entryId: string,
    patch: UpdateEntryInput
): Promise<CommunicationDetail> {
    const response = await apiV2.patch<{ success: true; data: CommunicationDetail }>(
        `${BASE_PATH}/${id}/entries/${entryId}`,
        patch
    );
    return response.data.data;
}

export async function deleteCommunicationEntry(id: string, entryId: string): Promise<void> {
    await apiV2.delete(`${BASE_PATH}/${id}/entries/${entryId}`);
}
